/**
 * AI-OCR (納品書) アップロード + ジョブ一覧画面。
 * フロー: PDF選択 → ブラウザ側でページごとにPNG変換 (lib/pdf-to-images.ts) →
 *   ジョブ作成 → ページ画像を順次アップロード → キュー投入 → 一覧に反映。
 * 詳細: docs/design/v02-ai-ocr-web-integration.md
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileScan, Upload, Loader2, Ban, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { convertPdfToPngPages } from '@/lib/pdf-to-images';
import { toast } from '@/stores/toast';
import { formatMonthLabel, formatElapsedSince } from '@/lib/format';
import { ConfirmDialog } from '@/components/Modal';
import { MonthPicker } from '@/components/MonthPicker';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };
type OcrJobStatus = 'uploaded' | 'queued' | 'processing' | 'pending_review' | 'completed' | 'failed' | 'cancelled';
type OcrJob = {
  job_id: string;
  store_id: string;
  target_month: string;
  status: OcrJobStatus;
  original_filename: string | null;
  page_count: number | null;
  processed_page_count: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

// デザインレビュー反映 (2026-08-02): 「動いているか分からない」というユーザー指摘への対応。
// 最終更新からこの分数以上進捗が無い場合、遅延の可能性を警告表示する (Nielsen H1: 可視性の原則)
const DELAY_WARNING_MINUTES = 3;

const STATUS_LABEL: Record<OcrJobStatus, string> = {
  uploaded: 'アップロード中',
  queued: '解析待ち',
  processing: '解析中',
  pending_review: 'レビュー待ち',
  completed: '完了',
  failed: '失敗',
  cancelled: '中止',
};
// デザインレビュー反映 (2026-08-02): KpiCard.tsx と同じ success/warning トークンに統一
const STATUS_CLASS: Record<OcrJobStatus, string> = {
  uploaded: 'bg-zinc-100 text-zinc-600',
  queued: 'bg-amber-50 text-amber-700',
  processing: 'bg-amber-50 text-amber-700',
  pending_review: 'bg-brand-red/10 text-brand-red',
  completed: 'bg-success-bg text-success-text',
  failed: 'bg-warning-bg text-warning-text',
  cancelled: 'bg-zinc-100 text-zinc-500 border border-zinc-300',
};
// ユーザー要望 (2026-08-02): 解析開始後でも店舗/対象月/ファイルの選択ミスに気づいたら中止できるようにする
const CANCELLABLE_STATUSES: OcrJobStatus[] = ['uploaded', 'queued', 'processing'];

const currentMonth = (): string => new Date().toISOString().slice(0, 7);

const authFetch = async (url: string, form: FormData) => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(url, { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  handleUnauthorized(res.status);
  const body = await res.json();
  if (!res.ok || !body.success) {
    throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー');
  }
  return body.data;
};

const UPLOAD_CONCURRENCY = 4;

export function AiOcrPage() {
  const qc = useQueryClient();
  const [storeId, setStoreId] = useState('');
  const [targetMonth, setTargetMonth] = useState(currentMonth());
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<{ phase: 'convert' | 'upload'; done: number; total: number } | null>(null);

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const activeStores = useMemo(() => stores.filter((s) => s.status === 'active'), [stores]);

  const { data: jobs = [], isLoading: jobsLoading } = useQuery<OcrJob[]>({
    queryKey: ['ai-ocr-jobs'],
    queryFn: () => apiFetch<OcrJob[]>('/ai/ocr/jobs'),
    refetchInterval: (query) => {
      const list = query.state.data ?? [];
      return list.some((j) => j.status === 'queued' || j.status === 'processing') ? 4000 : false;
    },
  });

  const storeName = (id: string) => stores.find((s) => s.store_id === id)?.store_name ?? id;

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!storeId) throw new Error('店舗を選択してください');
      if (!file) throw new Error('PDFファイルを選択してください');

      setProgress({ phase: 'convert', done: 0, total: 1 });
      const pages = await convertPdfToPngPages(file, (done, total) => setProgress({ phase: 'convert', done, total }));
      if (pages.length === 0) throw new Error('PDFにページが含まれていません');

      const jobForm = new FormData();
      jobForm.append('store_id', storeId);
      jobForm.append('target_month', targetMonth);
      jobForm.append('pdf', file);
      const job = await authFetch(`${API_BASE}/ai/ocr/jobs`, jobForm) as { job_id: string; duplicate_of_existing: boolean };

      if (job.duplicate_of_existing) {
        return { jobId: job.job_id, duplicate: true };
      }

      setProgress({ phase: 'upload', done: 0, total: pages.length });
      let uploaded = 0;
      let cursor = 0;
      const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, pages.length) }, async () => {
        while (cursor < pages.length) {
          const page = pages[cursor++]!;
          const pageForm = new FormData();
          pageForm.append('page_number', String(page.pageNumber));
          pageForm.append('image', page.blob, `page-${page.pageNumber}.png`);
          await authFetch(`${API_BASE}/ai/ocr/jobs/${job.job_id}/pages`, pageForm);
          uploaded += 1;
          setProgress({ phase: 'upload', done: uploaded, total: pages.length });
        }
      });
      await Promise.all(workers);

      await authFetch(`${API_BASE}/ai/ocr/jobs/${job.job_id}/enqueue`, new FormData());
      return { jobId: job.job_id, duplicate: false };
    },
    onSuccess: (result) => {
      setProgress(null);
      setFile(null);
      qc.invalidateQueries({ queryKey: ['ai-ocr-jobs'] });
      toast.success(result.duplicate
        ? '同一ファイルの取込ジョブが既に存在します (そちらを参照してください)'
        : 'アップロードが完了しました。解析が終わるとレビュー待ちになります');
    },
    onError: (e) => {
      setProgress(null);
      toast.error(e instanceof Error ? e.message : 'アップロードに失敗しました');
    },
  });

  const [cancelTarget, setCancelTarget] = useState<OcrJob | null>(null);
  const cancelMut = useMutation({
    mutationFn: (jobId: string) => apiFetch(`/ai/ocr/jobs/${jobId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('解析を中止しました');
      qc.invalidateQueries({ queryKey: ['ai-ocr-jobs'] });
      setCancelTarget(null);
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '中止に失敗しました');
      qc.invalidateQueries({ queryKey: ['ai-ocr-jobs'] });
      setCancelTarget(null);
    },
  });

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-h2 text-brand-black flex items-center gap-2"><FileScan size={22} /> AI-OCR (納品書取込)</h1>
        <p className="text-sm text-zinc-500 mt-1">納品書PDFをアップロードすると、AIが明細を読み取ります。読み取り結果は必ずレビュー画面で確認してから確定してください。</p>
      </div>

      <div className="p-6 rounded-xl border border-zinc-100 bg-white shadow-sm space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-neutral-text">店舗</span>
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              className="mt-1 w-full border border-neutral-line rounded px-3 py-2 text-sm"
            >
              <option value="">選択してください</option>
              {activeStores.map((s) => (
                <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-neutral-text">対象月</span>
            <MonthPicker
              aria-label="対象月"
              value={targetMonth}
              onChange={setTargetMonth}
              className="mt-1 w-full border border-neutral-line rounded px-3 py-2 text-sm"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-neutral-text">納品書PDF</span>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1 w-full text-sm"
          />
        </label>

        {progress && (
          <div className="text-sm text-neutral-sub">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {progress.phase === 'convert' ? 'PDFをページ画像に変換中…' : 'ページ画像をアップロード中…'}
              {' '}({progress.done}/{progress.total})
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
              <div
                className="h-full bg-brand-red transition-all"
                style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => uploadMut.mutate()}
          disabled={uploadMut.isPending || !storeId || !file}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-brand-red text-white text-sm font-medium disabled:opacity-50"
        >
          <Upload size={16} /> アップロードして解析開始
        </button>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-zinc-500">取込ジョブ一覧</h2>
        {jobsLoading ? (
          <div className="text-sm text-neutral-sub">読込中…</div>
        ) : jobs.length === 0 ? (
          <div className="p-6 text-center text-zinc-500 border border-zinc-100 rounded-xl bg-white shadow-sm text-sm">
            まだ取込ジョブがありません
          </div>
        ) : (
          <div className="border border-zinc-100 rounded-xl bg-white shadow-sm overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-zinc-50 text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">店舗</th>
                  <th className="text-left px-4 py-2 font-medium">対象月</th>
                  <th className="text-left px-4 py-2 font-medium">ファイル</th>
                  <th className="text-left px-4 py-2 font-medium">ページ数</th>
                  <th className="text-left px-4 py-2 font-medium">状態</th>
                  <th className="text-left px-4 py-2 font-medium">更新日時</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {[...jobs].reverse().map((j) => (
                  <tr key={j.job_id} className="border-t border-zinc-100">
                    <td className="px-4 py-2">{storeName(j.store_id)}</td>
                    <td className="px-4 py-2">{j.target_month}</td>
                    <td className="px-4 py-2 text-zinc-500 max-w-[220px] truncate" title={j.original_filename ?? undefined}>
                      {j.original_filename ?? '-'}
                    </td>
                    <td className="px-4 py-2">{j.page_count ?? '-'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[j.status]}`}>
                        {STATUS_LABEL[j.status]}
                      </span>
                      {j.status === 'processing' && (
                        <div className="text-xs text-zinc-500 mt-1">
                          {j.processed_page_count ?? 0}/{j.page_count ?? '?'}ページ処理中・最終更新: {formatElapsedSince(j.updated_at)}
                        </div>
                      )}
                      {(j.status === 'queued' || j.status === 'processing')
                        && (Date.now() - new Date(j.updated_at).getTime()) > DELAY_WARNING_MINUTES * 60 * 1000 && (
                        <div className="flex items-center gap-1 text-xs text-brand-orange mt-1">
                          <AlertTriangle size={12} /> 処理が遅延している可能性があります
                        </div>
                      )}
                      {j.status === 'failed' && j.error_message && (
                        <div className="text-xs text-red-600 mt-1">{j.error_message}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-zinc-500">{new Date(j.updated_at).toLocaleString('ja-JP')}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {(j.status === 'pending_review' || j.status === 'completed') && (
                        <Link to={`/ai-ocr/jobs/${j.job_id}`} className="text-brand-red hover:underline">
                          レビュー
                        </Link>
                      )}
                      {CANCELLABLE_STATUSES.includes(j.status) && (
                        <button
                          type="button"
                          onClick={() => setCancelTarget(j)}
                          className="inline-flex items-center gap-1 text-amber-700 hover:text-warning-text ml-3"
                        >
                          <Ban size={13} /> 中止
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={cancelTarget !== null}
        title="解析を中止しますか？"
        message={cancelTarget && (
          <div className="space-y-2">
            <p>以下のジョブの解析を中止します。この操作は<strong className="text-warning-text">取り消せません</strong>。</p>
            <dl className="text-sm bg-zinc-50 rounded p-3 space-y-1">
              <div className="flex gap-2"><dt className="text-zinc-500 w-16 shrink-0">店舗</dt><dd className="font-medium">{storeName(cancelTarget.store_id)}</dd></div>
              <div className="flex gap-2"><dt className="text-zinc-500 w-16 shrink-0">対象月</dt><dd className="font-medium">{formatMonthLabel(`${cancelTarget.target_month}-01`)}</dd></div>
              <div className="flex gap-2"><dt className="text-zinc-500 w-16 shrink-0">ファイル</dt><dd className="font-medium break-all">{cancelTarget.original_filename ?? '(不明)'}</dd></div>
            </dl>
          </div>
        )}
        confirmLabel="中止する"
        destructive
        loading={cancelMut.isPending}
        onConfirm={() => cancelTarget && cancelMut.mutate(cancelTarget.job_id)}
        onClose={() => setCancelTarget(null)}
      />
    </div>
  );
}
